/**
 * Utility functions to detect if the app is running in a WebView
 */

export const detectWebView = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Check for common WebView indicators
  const webViewIndicators = [
    // iOS WebView indicators
    /iPhone.*AppleWebKit(?!.*Safari)/i.test(userAgent),
    /iPad.*AppleWebKit(?!.*Safari)/i.test(userAgent),

    // Enhanced iPad detection for native apps
    // Modern iPads (iPadOS 13+) often report desktop Safari UA, so we need multiple checks
    (() => {
      const isIPad =
        /iPad/i.test(userAgent) ||
        // Detect iPad with desktop user agent (iPadOS 13+)
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      if (!isIPad) return false;

      // If it's an iPad, check for WebView indicators
      return (
        // No Safari in user agent
        !/Safari/i.test(userAgent) ||
        // No window.safari object (WebView indicator)
        (typeof window !== 'undefined' && !window.safari) ||
        // Check for WKWebView specific properties
        (typeof window !== 'undefined' &&
          typeof window.webkit !== 'undefined' &&
          typeof window.webkit.messageHandlers !== 'undefined') ||
        // Check for standalone mode but not a PWA (likely native app)
        (typeof window !== 'undefined' &&
          window.navigator.standalone === true &&
          !window.matchMedia('(display-mode: standalone)').matches)
      );
    })(),

    // Android WebView indicators
    /Android.*wv\)/i.test(userAgent),
    /Version\/[\d\.]+.*Chrome\/[.\d]+.*Mobile/i.test(userAgent),

    // React Native WebView
    /ReactNative/i.test(userAgent),

    // Cordova/PhoneGap
    /cordova/i.test(userAgent),
    /phonegap/i.test(userAgent),

    // Ionic
    /ionic/i.test(userAgent),

    // Other mobile app WebViews
    /FBAN|FBAV/i.test(userAgent), // Facebook
    /Instagram/i.test(userAgent), // Instagram
    /Twitter/i.test(userAgent), // Twitter
    /LinkedIn/i.test(userAgent), // LinkedIn

    // Check if window.webkit exists (iOS WebView)
    typeof window !== 'undefined' &&
      typeof window.webkit !== 'undefined' &&
      typeof window.webkit.messageHandlers !== 'undefined',

    // Check if Android interface exists
    typeof window !== 'undefined' && typeof window.Android !== 'undefined',

    // Check for lack of certain browser features
    typeof window !== 'undefined' &&
      !window.chrome &&
      /Chrome/i.test(userAgent),
  ];

  return webViewIndicators.some((indicator) => indicator);
};

export const isStandaloneApp = () => {
  // Check if running as PWA in standalone mode
  return (
    typeof window !== 'undefined' &&
    (window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches)
  );
};

export const getMobileAppContext = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Enhanced iPad detection for modern devices
  const isIPad =
    /iPad/i.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  return {
    isWebView: detectWebView(),
    isStandalone: isStandaloneApp(),
    isIOS: /iPad|iPhone|iPod/.test(userAgent) || isIPad,
    isAndroid: /Android/.test(userAgent),
    isMobile: /Mobi|Android/i.test(userAgent) || isIPad,
    isReactNative: /ReactNative/i.test(userAgent),
    isCordova: /cordova/i.test(userAgent),
    isIonic: /ionic/i.test(userAgent),
    userAgent,
  };
};
